import React, { useContext, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FiCheck, FiEdit2, FiX } from "react-icons/fi";
import moment from "moment";
import { patchApp } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";

const formatTs = (ts) => (ts ? moment(ts).format("DD-MM-YYYY HH:mm") : "—");

const Row = ({ label, value }) => (
  <div className="row mb-3">
    <div className="col-sm-3 text-muted">{label}</div>
    <div className="col-sm-9">{value}</div>
  </div>
);

const OverviewTab = ({ app, onUpdated }) => {
  const { showSuccess, showError } = useContext(ToastrContext);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(app.name || "");
  const [saving, setSaving] = useState(false);

  const enterEdit = () => {
    setDraft(app.name || "");
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(app.name || "");
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      showError("Name cannot be empty");
      return;
    }
    if (trimmed === app.name) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await patchApp(app.public_app_id, { name: trimmed });
      showSuccess("App updated");
      setEditing(false);
      onUpdated(updated);
    } catch (e) {
      showError((e && e.message) || "Failed to update app");
    } finally {
      setSaving(false);
    }
  };

  const statusBadgeClass = (() => {
    switch (app.status) {
      case "active":
        return "bg-success";
      case "suspended":
        return "bg-warning";
      case "revoked":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  })();

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        {editing ? (
          <>
            <input
              type="text"
              className="form-control form-control-lg me-2"
              value={draft}
              maxLength={255}
              autoFocus
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              style={{ maxWidth: 480 }}
            />
            <Button
              variant="primary"
              onClick={save}
              disabled={saving}
              className="me-1"
              title="Save"
            >
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiCheck />
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={cancelEdit}
              disabled={saving}
              title="Cancel"
            >
              <FiX />
            </Button>
          </>
        ) : (
          <>
            <h4 className="mb-0 me-2">{app.name}</h4>
            <Button
              variant="link"
              className="p-0 text-muted"
              onClick={enterEdit}
              title="Edit name"
            >
              <FiEdit2 size={16} />
            </Button>
          </>
        )}
      </div>

      <Row
        label="Status"
        value={
          <span className={`badge ${statusBadgeClass}`}>{app.status}</span>
        }
      />
      <Row label="App ID" value={<code>{app.public_app_id}</code>} />
      <Row label="Type" value={app.type || "—"} />
      <Row label="API Version" value={app.api_version || "—"} />
      <Row label="Plan Tier" value={app.plan_tier || "—"} />
      <Row label="Description" value={app.description || "—"} />
      <Row
        label="Homepage URL"
        value={
          app.homepage_url ? (
            <a href={app.homepage_url} target="_blank" rel="noreferrer">
              {app.homepage_url}
            </a>
          ) : (
            "—"
          )
        }
      />
      <Row label="Created" value={formatTs(app.ts_created)} />
      <Row label="Last Used" value={formatTs(app.ts_last_used)} />
    </div>
  );
};

export default OverviewTab;
